import React from 'react';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { colors, typo } from '../../../../theme/tokens';
import { StatusChip, WriteIconButton } from '../../../../components/ui';
import { RbacRole } from '../../../../api/rolesV2Api';

interface Props {
  role: RbacRole;
  /** Custom roles — edit/delete. */
  onEdit?: (role: RbacRole) => void;
  onDelete?: (role: RbacRole) => void;
  /** System roles — read-only view of the locked permission set. */
  onView?: (role: RbacRole) => void;
}

/**
 * Persona card for the Users > Roles tab (RBAC_V2). Shows the role name, a
 * System/Custom badge, description, its permission count, and how many
 * users hold it. A zero-permission role is allowed but visibly flagged.
 *
 * System roles (Default, Super Admin) are locked: no edit/delete — only a
 * read-only "View" of their permission set. Custom roles expose edit +
 * delete (delete is guarded server-side, decisions §19 — blocked while any
 * user holds the role).
 */
const RolePersonaCard: React.FC<Props> = ({ role, onEdit, onDelete, onView }) => {
  const permCount = role.permissions.length;
  const zeroPerms = permCount === 0;
  const assignedUserCount = role.assigned_user_count ?? 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: '12px',
        border: `1px solid ${colors.borderLight}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: '100%',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)', borderColor: colors.borderHover },
      }}
    >
      {/* Header: name + System/Custom badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Tooltip title={role.role_name}>
          <Typography sx={{ ...typo.sectionTitle, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {role.role_name}
          </Typography>
        </Tooltip>
        {role.is_system ? (
          <StatusChip status="info" label="System" icon={<LockIcon sx={{ fontSize: 12 }} />} />
        ) : (
          <Chip label="Custom" size="small" variant="outlined" sx={{ fontSize: 10, fontFamily: 'Manrope, sans-serif', flexShrink: 0 }} />
        )}
      </Box>

      {/* Description — clamped to 2 lines; full text on hover since several
          descriptions run longer than that (e.g. multi-surface role grants). */}
      <Tooltip title={role.description || 'No description.'}>
        <Typography
          sx={{
            ...typo.smallText,
            color: colors.textSecondary,
            flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {role.description || 'No description.'}
        </Typography>
      </Tooltip>

      {/* Permission count / zero-permission flag */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {zeroPerms ? (
          <Chip
            size="small"
            icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
            label="No permissions"
            sx={{ height: 22, fontSize: 11, fontWeight: 700, color: colors.warningText, backgroundColor: colors.warningBg, '& .MuiChip-icon': { color: colors.warningText } }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary }}>
            <KeyOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {permCount} permission{permCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer: holders + (custom) edit/delete or (system) view + lock */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${colors.borderLight}`, pt: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary }}>
          <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            Held by {assignedUserCount} user{assignedUserCount === 1 ? '' : 's'}
          </Typography>
        </Box>
        {role.is_system ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {onView && (
              <Tooltip title="View permissions">
                <span>
                  <WriteIconButton
                    ignoreEnvReadOnly
                    size="small"
                    aria-label={`View permissions — ${role.role_name}`}
                    onClick={() => onView(role)}
                    sx={{ color: colors.textSecondary }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                  </WriteIconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="System role — locked">
              <LockIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {onEdit && (
              <Tooltip title="Edit role">
                <span>
                  <WriteIconButton
                    ignoreEnvReadOnly
                    size="small"
                    aria-label={`Edit role — ${role.role_name}`}
                    onClick={() => onEdit(role)}
                    sx={{ color: colors.textSecondary }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </WriteIconButton>
                </span>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={assignedUserCount > 0 ? `${assignedUserCount} user${assignedUserCount === 1 ? '' : 's'} still hold this role` : 'Delete role'}>
                <span>
                  <WriteIconButton
                    ignoreEnvReadOnly
                    size="small"
                    aria-label={`Delete role — ${role.role_name}`}
                    onClick={() => onDelete(role)}
                    sx={{ color: colors.errorIcon }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </WriteIconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default RolePersonaCard;
